import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogPost } from '../../contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import '../../styles/blog-overrides.scss';
import andyPhoto from '../../img/profile/small_andy-bluebg-square.png';

// Mock data for testing - moved outside component to avoid dependency issues
const mockPost = {
  id: '1',
  slug: 'test-post-1',
  title: 'When Good Plans Go Sideways: Our Unplanned Descent Down the Wilderness Channel',
  description: 'A thrilling adventure story about unexpected challenges and how we overcame them in the wilderness.',
  content: {
    nodeType: 'document',
    data: {},
    content: [
      {
        nodeType: 'heading-2',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'The Adventure Begins',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'It was supposed to be a simple day trip down the river. We had planned everything meticulously - the route, the timing, the equipment. But as any experienced adventurer knows, the best-laid plans often go awry.',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'heading-3',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Unexpected Challenges',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'The weather had other ideas. What started as a clear morning quickly turned into a storm that would test our skills and determination. The river swelled, and our planned route became impassable.',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'heading-3',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Adapting to Change',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Instead of panicking, we had to quickly adapt our strategy. This meant finding alternative routes, adjusting our timeline, and making decisions on the fly. It was a perfect example of how flexibility and quick thinking can save the day.',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'heading-3',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Lessons Learned',
            marks: [],
            data: {}
          }
        ]
      },
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'This experience taught us valuable lessons about preparation, adaptability, and the importance of having backup plans. Sometimes the most memorable adventures are the ones that don\'t go according to plan.',
            marks: [],
            data: {}
          }
        ]
      }
    ]
  },
  publishedAt: '2025-08-06',
  author: 'Andy Cary',
  tags: ['Adventure Stories', 'Wilderness', 'Challenges'],
  featuredImage: {
    url: 'https://via.placeholder.com/800x400/3b82f6/ffffff?text=Adventure+Story',
    alt: 'Wilderness adventure'
  }
};

const BlogPost = () => {
  const { shortName } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Rich text renderer options
  const richTextOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className="mb-4 text-slate-400 leading-relaxed">{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1 className="text-3xl font-bold text-slate-100 mb-4 mt-8">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="text-2xl font-bold text-slate-100 mb-3 mt-6">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="text-xl font-bold text-slate-100 mb-3 mt-6">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className="text-lg font-bold text-slate-100 mb-2 mt-4">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <h5 className="text-base font-bold text-slate-100 mb-2 mt-4">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className="text-sm font-bold text-slate-100 mb-2 mt-4">{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="text-slate-400">{children}</li>
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <blockquote className="border-l-4 border-accent pl-4 italic text-slate-400 mb-4">{children}</blockquote>
      ),
      [BLOCKS.HR]: () => (
        <hr className="my-8 border-accent/20" />
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a href={node.data.uri} className="text-accent hover:text-accent/90 underline" target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { url, description } = node.data.target.fields;
        return (
          <div className="my-6">
            <img 
              src={url.startsWith('//') ? `https:${url}` : url} 
              alt={description || 'Embedded content'} 
              className="w-full h-auto rounded-lg shadow-md"
            />
          </div>
        );
      }
    }
  };

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const blogPost = await getBlogPost(shortName);
      
      if (!blogPost) {
        setPost(mockPost);
      } else {
        setPost(blogPost);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(mockPost);
      // Don't set error, just use mock data
    } finally {
      setLoading(false);
    }
  }, [shortName]);

  useEffect(() => {
    if (shortName) {
      fetchPost();
      // Scroll to top when navigating to a new blog post
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [fetchPost, shortName]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.description,
          url: window.location.href
        });
      } catch (error) {
        // Fallback to clipboard if sharing is cancelled
        await handleCopyToClipboard();
      }
    } else {
      await handleCopyToClipboard();
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent/20 border-t-accent" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-100 mb-4">Something went wrong</h1>
          <p className="text-slate-400 mb-8">{error}</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-accent text-background-dark rounded-lg hover:bg-accent/90 transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold text-slate-100 mb-4">Post not found</h1>
          <p className="text-slate-400 mb-8">The blog post you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-accent text-background-dark rounded-lg hover:bg-accent/90 transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pb-8">
      {/* Back to Blog Button */}
      <div className="mb-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-accent hover:text-accent/90 font-medium transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Blog
        </Link>
      </div>

      {/* Blog Post Content */}
      <article className="max-w-4xl mx-auto bg-slate-800/30 rounded-2xl shadow-xl border border-accent/10 p-6 sm:p-8 lg:p-12">
        {/* Header */}
        <header className="mb-8">
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-accent/10 text-accent text-sm font-semibold rounded-full border border-accent/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-100 mb-6 leading-tight">
            {post.title}
          </h1>
          
          <p className="text-base sm:text-lg lg:text-xl text-slate-400 mb-6 leading-relaxed">
            {post.description}
          </p>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-slate-400 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-background-dark text-sm font-bold">
                {post.author ? post.author.charAt(0).toUpperCase() : 'A'}
              </div>
              <span className="font-medium">{post.author}</span>
              <span className="hidden sm:inline">•</span>
              <span>{formatDate(post.publishedAt)}</span>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Download Button for App Release */}
              {post.appRelease && post.appRelease.downloadLink && (
                <a
                  href={post.appRelease.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{post.appRelease.appName}</span>
                </a>
              )}
              
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 text-slate-400 hover:text-accent transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span>{copied ? 'Copied!' : 'Share'}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.featuredImage && (
          <div className="mb-8">
            <img
              src={post.featuredImage.url}
              alt={post.featuredImage.alt}
              className="w-full h-auto rounded-2xl shadow-xl"
              loading="lazy"
              onError={handleImageError}
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none blog-post-content">
          {post.content ? (
            <div className="text-slate-400 leading-relaxed">
              {documentToReactComponents(post.content, richTextOptions)}
            </div>
          ) : (
            <div className="text-slate-400 leading-relaxed space-y-4">
              <p>This is a sample blog post content. In a real application, this would contain the full article content.</p>
              <p>The content would be formatted with proper HTML markup and styling.</p>
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
              <p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
            </div>
          )}
        </div>

        {/* Action Button Section */}
        {post.appRelease && post.appRelease.downloadLink && (
          <div className="mt-12 pt-8 border-t border-accent/10">
            <div className="text-center space-y-6">
              <div className="flex justify-center items-center space-x-4">
                <div className="w-2 h-2 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"></div>
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent"></div>
                <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-100">
                  {post.appRelease.appName}
                </h3>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Explore this content in different formats.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a
                  href={post.appRelease.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-button-post inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-premium border border-emerald-500/30 hover:border-emerald-400/50 shadow-sm"
                  style={{ 
                    '--tw-text-opacity': '1',
                    'color': 'rgb(255 255 255)'
                  }}
                >
                  <svg 
                    className="w-5 h-5" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    style={{ 
                      '--tw-text-opacity': '1',
                      'color': 'rgb(255 255 255)'
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span 
                    style={{ 
                      '--tw-text-opacity': '1',
                      'color': 'rgb(255 255 255)'
                    }}
                  >
                    {post.appRelease.appName}
                  </span>
                </a>
                
                <button
                  onClick={handleShare}
                  className="inline-flex items-center space-x-2 bg-accent/10 hover:bg-accent/20 text-accent font-semibold px-6 py-3 rounded-xl border border-accent/20 hover:border-accent/30 shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  <span>{copied ? 'Copied!' : 'Share Article'}</span>
                </button>
              </div>
              
              <div className="flex justify-center items-center space-x-4">
                <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent"></div>
                <div className="w-2 h-2 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"></div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t border-accent/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 sm:gap-4 px-4 sm:px-6 py-1">
            <Link to="/about" className="flex items-center gap-4 group">
              {post.author === 'Andy Cary' ? (
                <img
                  src={andyPhoto}
                  alt="Andy Cary"
                  className="w-12 h-12 rounded-full object-cover border-2 border-accent/40 shrink-0"
                />
              ) : (
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-background-dark text-lg font-bold shrink-0">
                  {post.author ? post.author.charAt(0).toUpperCase() : 'A'}
                </div>
              )}
              <div>
                <p className="font-semibold text-slate-100 group-hover:text-accent transition-colors">{post.author}</p>
                <p className="text-sm text-slate-400">Author</p>
              </div>
            </Link>

            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent text-background-dark rounded-lg hover:bg-accent/90 hover:shadow-lg transition-all duration-300 text-center w-full sm:w-auto"
            >
              Back to Blog
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogPost; 